import { useQueryClient } from '@tanstack/react-query'
import { queryKeysForTable, serverMessageSchema } from '@fieldops/shared'
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { markReachable } from './trpc.js'

/**
 * The live connection.
 *
 * The server sends identifiers only — table, operation, id — and this turns
 * them into TanStack Query invalidations. The refetch then goes through the
 * normal authorised read path, so nothing arrives over the socket that the
 * person is not allowed to see.
 *
 * Invalidating rather than patching in place is deliberate. Patching means
 * reimplementing every server-side filter, sort and permission rule in the
 * client and keeping the two in agreement forever; the first time they
 * disagree, someone sees a row that should not be on their screen. An
 * invalidation is one extra request and cannot be wrong.
 *
 * It can, however, be *untimely*, and enough untimely ones are indistinguishable
 * from an outage — which is what decision 30 diagnosed. Every write fires
 * `pg_notify`; a bulk import produces thirty a second; each one invalidated
 * immediately, and `invalidateQueries` defaults to `cancelRefetch: true`, so
 * every notification aborted the fetch the last one started and the query never
 * completed at all. The board died with `ERR_INSUFFICIENT_RESOURCES`.
 *
 * So the invalidation stays and two things go around it: changes are collected
 * and flushed on a trailing debounce, and a flush does not cancel a fetch
 * already in flight. Thirty writes in a second become one refetch that finishes.
 */

export type ConnectionState = 'connecting' | 'open' | 'closed'

/**
 * How long a change waits for company before it is acted on.
 *
 * Long enough that a burst coalesces, short enough that a dispatcher moving a
 * booking sees the other screens follow within a beat. A quarter second is
 * under the threshold where a screen reads as stale.
 */
const COALESCE_MS = 250

const RealtimeContext = createContext<ConnectionState>('closed')

export const useConnectionState = (): ConnectionState => useContext(RealtimeContext)

export function RealtimeProvider({
  children,
  enabled,
  token,
}: {
  children: ReactNode
  enabled: boolean
  token?: string | null
}) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<ConnectionState>('closed')
  const attemptRef = useRef(0)

  useEffect(() => {
    if (!enabled) {
      setState('closed')
      return
    }

    let socket: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let heartbeat: ReturnType<typeof setInterval> | undefined
    let disposed = false

    // Tables changed since the last flush. A Set, so a hundred writes to one
    // table are one entry and one refetch.
    const pending = new Set<string>()
    let flushTimer: ReturnType<typeof setTimeout> | undefined

    const flush = () => {
      flushTimer = undefined
      if (pending.size === 0) return

      const tables = [...pending]
      pending.clear()

      // De-duplicated across tables too: `Job` and `ScheduledVisit` both
      // invalidate the board, and invalidating it twice is two fetches.
      const keys = new Map<string, readonly unknown[]>()
      for (const table of tables) {
        for (const queryKey of queryKeysForTable(table)) {
          keys.set(JSON.stringify(queryKey), queryKey)
        }
      }

      for (const queryKey of keys.values()) {
        // Never cancel a fetch already on the wire. Cancelling is what turned
        // a burst into a query that never resolved.
        void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false })
      }
    }

    const schedule = (table: string) => {
      pending.add(table)
      if (flushTimer === undefined) flushTimer = setTimeout(flush, COALESCE_MS)
    }

    const connect = () => {
      if (disposed) return
      setState('connecting')

      const base = import.meta.env['VITE_API_URL'] ?? window.location.origin
      const url = new URL('/realtime', base)
      url.protocol = url.protocol.replace('http', 'ws')
      // Only the native build carries a token; the web build authenticates
      // with the session cookie, which the browser attaches automatically.
      if (token) url.searchParams.set('token', token)

      socket = new WebSocket(url)

      socket.onopen = () => {
        // Back after a drop. Whatever changed meanwhile was missed, and a
        // screen whose query gave up while the server was gone is still
        // showing its error: refetch everything active, as on coming back
        // to the tab.
        if (attemptRef.current > 0) {
          void queryClient.invalidateQueries(undefined, { cancelRefetch: false })
        }
        attemptRef.current = 0
        markReachable()
        setState('open')
        // Intermediaries close idle sockets without telling anyone. A ping
        // every thirty seconds keeps the connection from quietly dying.
        heartbeat = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }))
          }
        }, 30_000)
      }

      socket.onmessage = (event) => {
        const parsed = serverMessageSchema.safeParse(JSON.parse(String(event.data)))
        if (!parsed.success || parsed.data.type !== 'change') return

        schedule(parsed.data.table)
      }

      socket.onclose = () => {
        clearInterval(heartbeat)
        setState('closed')
        if (disposed) return

        attemptRef.current += 1
        const delay = Math.min(30_000, 500 * 2 ** Math.min(attemptRef.current, 6))
        reconnectTimer = setTimeout(connect, delay)
      }

      socket.onerror = () => socket?.close()
    }

    connect()

    // Coming back from a locked phone: the socket is usually dead and its
    // close event never fired, and anything that changed while away was
    // missed. Refetch everything active, then reconnect.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void queryClient.invalidateQueries(undefined, { cancelRefetch: false })
      if (socket?.readyState !== WebSocket.OPEN) {
        socket?.close()
        connect()
      }
    }

    document.addEventListener('visibilitychange', onVisible)

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisible)
      clearTimeout(reconnectTimer)
      clearTimeout(flushTimer)
      clearInterval(heartbeat)
      socket?.close()
    }
  }, [enabled, token, queryClient])

  return <RealtimeContext.Provider value={state}>{children}</RealtimeContext.Provider>
}
